import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useFundIdFromPath from '@/hooks/useFundIdFromPath';
import { useQuery } from '@tanstack/react-query';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { getAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import useInquiryQueueCount from '@/hooks/useInquiryQueueCount';
import useInquiriesFeature from '@/hooks/useInquiriesFeature';
import styles from './InquiryQueueIndicator.module.scss';

/**
 * The way into the correspondence centre from the site header, with the number waiting.
 *
 * ⚠️ Shown to COORDINATORS as well as the office, and that is the point of this note. It was
 * office-only at first, on the reasoning that the queue is the office's. But the coordinator
 * gets a chime too (CoordinatorReplyAlert), so they need the way in as much as the office does.
 *
 * ⚠️ The alert settings used to sit here, as a second little bell beside the capsule. They moved
 * to the inquiries screen itself (InquiriesPage), because the updates bell (HAT-1099) now owns
 * the bell glyph in this header and two bells 40px apart meaning different things is a guess, not
 * a control. The reason they were put here in the first place - a chime with no visible way to
 * silence it - is answered instead by revealing the settings once, on the first visit to that
 * screen: the settings are per browser, so a first visit is the right moment and the only one
 * that needs teaching.
 *
 * The two see different numbers, because they are waiting for different things: the office sees
 * the queue it has to answer, a coordinator sees their own unread replies. The label and the
 * destination differ with them.
 */

export default function InquiryQueueIndicator() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuthContext();
  const isFeatureOn = useInquiriesFeature();
  const { count: officeCount, canAccess: isOffice } = useInquiryQueueCount();
  const fundId = useFundIdFromPath();
  const coordinatorUrl = `funds/${fundId}/inquiries/unread-count`;
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  /*
   * The coordinator's own unread replies. Same key as SidebarBadgeProvider and
   * CoordinatorReplyAlert, so all three read one cached value and the count is fetched once.
   * Disabled for the office, which has its own number and does not need this one.
   */
  const { data: coordinatorUnread } = useQuery({
    queryKey: [coordinatorUrl],
    queryFn: ({ signal }) => getAuth<{ count: number }>(coordinatorUrl, headers, undefined, signal),
    enabled: isFeatureOn && !isOffice && !!fundId && !!currentUser?.token,
    staleTime: 0,
    refetchInterval: 30000,
  });

  /*
   * Below the hooks, so the hook order stays identical whether or not this renders.
   * With inquiries off there is nowhere for the capsule to lead, so the header carries none.
   */
  if (!isFeatureOn) return null;

  // No fund and not the office: there is nothing this control could point at.
  if (!isOffice && !fundId) return null;

  const count = isOffice ? officeCount : coordinatorUnread?.count;

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={`body-sm-semibold ${styles.link}`}
        onClick={() => navigate(isOffice ? '/management/inquiries' : `/${fundId}/support/inquiries`)}
        title={t(isOffice ? 'inquiries.queue.goToAll' : 'inquiries.queue.goToMine')}
      >
        <ForumOutlinedIcon sx={{ fontSize: 18 }} />
        <span className={styles.label}>{t('inquiries.queue.label')}</span>
        {/* undefined עד התשובה הראשונה - ואז אין מה להציג, גם לא 0 */}
        {count !== undefined && count > 0 && <span className={styles.count}>{count}</span>}
      </button>
    </div>
  );
}
